/**
 * Every effect M6 adds, and the rules about when each one appears.
 *
 * One object rather than six, because they share two particle systems and one tracer buffer — and
 * because the *policies* (when does a contrail form, when do the wingtips condense) belong
 * together with the thing they emit into. Split across six modules, "why is there no contrail" has
 * six places to look.
 *
 * ## The physics behind the two conditional effects
 *
 * **Contrails.** A contrail is not smoke; it is cloud. An engine's exhaust is mostly water vapour,
 * and when it mixes with air that is cold enough, the mixture crosses saturation and the water
 * condenses and freezes. That needs roughly −40 °C, which in a standard atmosphere is around
 * 26,000–30,000 ft — hence the threshold, which is a *temperature* threshold wearing an altitude
 * costume. Below it, nothing, no matter how hard the engine is working.
 *
 * **Wingtip vortices.** Every wing sheds a vortex; you can only *see* it when the pressure drop in
 * the core is enough to condense the ambient humidity. That drop scales with the lift coefficient,
 * so the trigger is high alpha or high g — which is why they appear in a hard turn and not in
 * cruise, and why they are more visible on a humid day. Both terms are in the emission rule below.
 *
 * ## Budget
 *
 * Two draw calls for all the particles, one for the tracers, and exactly one point light for the
 * explosion flash. That is the entire cost of this milestone's effects in draw calls, which
 * matters against a budget of 350 with the terrain already using most of it.
 *
 * The light being *one*, and never switching off, is not a budget decision but a correctness one —
 * see the note on {@link CombatEffects.flash}. It is the difference between a smooth frame and a
 * 173 ms stall every time something explodes.
 *
 * `updateFrame(alpha, dt)`: yes — {@link CombatEffects.updateFrame}. Allocation-free after
 * construction; every particle comes from a pool filled at load.
 */

import { Color, Group, PointLight, Vector3 } from 'three';
import { clamp01 } from '../../core/math.js';
import type { Rng } from '../../core/rng.js';
import { ParticleSystem, type ParticleSpec } from './particles.js';
import { TracerRibbons } from './tracers.js';

/** Above this, exhaust condenses and freezes. See the module header. */
export const CONTRAIL_ALTITUDE_M = 7_925;

// --- the specs ---------------------------------------------------------------------------------
// Deliberately module constants: a spec is read on every spawn and must never be built per call.

/** White-hot fragments off an impact. Short, fast, gravity-affected. */
const SPARK: ParticleSpec = {
  lifetimeS: 0.45,
  startRadiusM: 0.35,
  endRadiusM: 0.06,
  startColour: [6.0, 3.4, 1.1],
  endColour: [1.6, 0.4, 0.06],
  startAlpha: 1,
  endAlpha: 0,
  drag: 2.2,
  buoyancyMps2: 0,
  gravityScale: 1,
};

/** The fireball: brief, enormous, and the thing the bloom catches. */
const FIREBALL: ParticleSpec = {
  lifetimeS: 0.9,
  startRadiusM: 3.5,
  endRadiusM: 14,
  startColour: [9.0, 4.2, 1.0],
  endColour: [1.2, 0.35, 0.08],
  startAlpha: 1,
  endAlpha: 0,
  drag: 3.2,
  buoyancyMps2: 6,
  gravityScale: 0,
};

/** The column that stands afterwards. Long-lived, dark, rising. */
const EXPLOSION_SMOKE: ParticleSpec = {
  lifetimeS: 7,
  startRadiusM: 4,
  endRadiusM: 26,
  startColour: [0.10, 0.09, 0.085],
  endColour: [0.34, 0.33, 0.32],
  startAlpha: 0.85,
  endAlpha: 0,
  drag: 0.9,
  buoyancyMps2: 2.4,
  gravityScale: 0,
};

/** A damaged drone trails this before it dies. */
const DAMAGE_SMOKE: ParticleSpec = {
  lifetimeS: 2.4,
  startRadiusM: 0.9,
  endRadiusM: 6,
  startColour: [0.12, 0.11, 0.10],
  endColour: [0.4, 0.39, 0.38],
  startAlpha: 0.6,
  endAlpha: 0,
  drag: 0.7,
  buoyancyMps2: 1.2,
  gravityScale: 0,
};

/** Ice crystals. White, bright, long-lived, and they spread. */
const CONTRAIL: ParticleSpec = {
  lifetimeS: 26,
  startRadiusM: 2.2,
  endRadiusM: 34,
  startColour: [0.95, 0.96, 1.0],
  endColour: [0.88, 0.90, 0.95],
  startAlpha: 0.5,
  endAlpha: 0,
  drag: 0.5,
  buoyancyMps2: 0,
  gravityScale: 0,
};

/** The condensation core off a wingtip: thin, short-lived, and it follows the wing. */
const VORTEX: ParticleSpec = {
  lifetimeS: 1.6,
  startRadiusM: 0.5,
  endRadiusM: 3.4,
  startColour: [0.92, 0.94, 0.99],
  endColour: [0.85, 0.88, 0.95],
  startAlpha: 0.45,
  endAlpha: 0,
  drag: 1.1,
  buoyancyMps2: 0,
  gravityScale: 0,
};

/** A rocket motor's trail: dense and bright while it burns. */
const MOTOR_SMOKE: ParticleSpec = {
  lifetimeS: 4.5,
  startRadiusM: 1.1,
  endRadiusM: 11,
  startColour: [0.72, 0.70, 0.68],
  endColour: [0.5, 0.5, 0.5],
  startAlpha: 0.8,
  endAlpha: 0,
  drag: 1.4,
  buoyancyMps2: 0.6,
  gravityScale: 0,
};

/** What a missile leaves once the motor is out — much thinner. */
const COAST_SMOKE: ParticleSpec = {
  lifetimeS: 2.6,
  startRadiusM: 0.5,
  endRadiusM: 4,
  startColour: [0.66, 0.66, 0.66],
  endColour: [0.55, 0.55, 0.55],
  startAlpha: 0.25,
  endAlpha: 0,
  drag: 1.6,
  buoyancyMps2: 0.3,
  gravityScale: 0,
};

/** The flame at a burning motor's nozzle. */
const MOTOR_FLAME: ParticleSpec = {
  lifetimeS: 0.18,
  startRadiusM: 1.4,
  endRadiusM: 0.4,
  startColour: [7.0, 3.6, 1.2],
  endColour: [2.0, 0.7, 0.15],
  startAlpha: 1,
  endAlpha: 0,
  drag: 5,
  buoyancyMps2: 0,
  gravityScale: 0,
};

export interface CombatEffectsOptions {
  rng: Rng;
  /**
   * Build the explosion flash light at all.
   *
   * False for an aircraft that carries no weapons: it still wants contrails and wingtip
   * vortices, and it can never detonate anything, so it should not pay for a point light in
   * every lit shader in the scene for the whole session.
   */
  flashLight?: boolean;
  /** Particle ceilings. Tuned down by the quality tier — see {@link CombatEffects.applyQuality}. */
  smokeCapacity?: number;
  fireCapacity?: number;
  tracerCapacity?: number;
}

const scratchPosition = /* #__PURE__ */ new Vector3();
const scratchVelocity = /* #__PURE__ */ new Vector3();

/** How fast a flash decays [s]. An explosion's light is a genuine transient. */
const FLASH_DECAY_S = 0.09;
/** Below this the flash is treated as over and the intensity is snapped to zero. */
const FLASH_CUTOFF = 1;

export class CombatEffects {
  readonly group = new Group();
  readonly tracers: TracerRibbons;

  private readonly smoke: ParticleSystem;
  private readonly fire: ParticleSystem;
  private readonly rng: Rng;
  /**
   * The explosion flash — **one** light, created visible and never hidden.
   *
   * ## Why it is one, and why it never switches off
   *
   * Three bakes the scene's *set of lights* into every lit material's shader program. Changing
   * how many lights are visible therefore invalidates every material in the scene and rebuilds
   * every pipeline, synchronously, inside `render()`. This is not a small effect: measured on the
   * real renderer with the terrain resident, one light appearing cost **173 ms**, and a perf run
   * caught a single frame of **1153 ms**. The first version of this pooled four lights and
   * toggled `visible` per explosion, so it paid that twice a bang — which is precisely the
   * "very jittery" this exists to have fixed.
   *
   * So the light count is a constant of the scene. The light is created visible with an intensity
   * of zero, and a flash is a change of *intensity and position* — both uniforms, neither of which
   * touches a program. `combatEffects.test.ts` asserts the count never moves.
   *
   * One rather than four for the same reason it is not zero: every light in the scene costs
   * evaluation in every lit fragment for the whole session, so the effect should own the smallest
   * number that delivers it. Two explosions close enough to overlap are lit by whichever is
   * brighter, and nobody has ever been able to tell.
   */
  private readonly flash: PointLight | null;
  private flashPeak = 0;
  private flashAgeS = 0;

  /** Emission budget carried between frames, so rates are frame-rate independent. */
  private contrailAccumulator = 0;
  private vortexAccumulator = 0;
  /** 0…1, scaled down at low quality tiers. */
  private densityScale = 1;

  constructor(options: CombatEffectsOptions) {
    this.rng = options.rng;
    this.group.name = 'combat-effects';

    this.smoke = new ParticleSystem({
      capacity: options.smokeCapacity ?? 3_000,
      blending: 'alpha',
      softness: 0.95,
      name: 'combat-smoke',
    });
    this.fire = new ParticleSystem({
      capacity: options.fireCapacity ?? 900,
      blending: 'additive',
      softness: 0.8,
      name: 'combat-fire',
    });
    this.tracers = new TracerRibbons({ capacity: options.tracerCapacity ?? 220 });

    this.group.add(this.smoke.cloud.mesh, this.fire.cloud.mesh, this.tracers.mesh);

    if (options.flashLight === false) {
      this.flash = null;
    } else {
      const light = new PointLight(new Color(1.0, 0.62, 0.28), 0, 900, 2);
      /*
       * Visible from birth and never touched again. Adding it here — before the scene's
       * `precompile` pass runs — means every material is compiled *with* it once, at boot,
       * behind the splash, instead of the first explosion doing it mid-flight.
       */
      light.visible = true;
      light.castShadow = false;
      this.group.add(light);
      this.flash = light;
    }
  }

  /** Current flash brightness. Exposed so the regression test can see it without a renderer. */
  get flashIntensity(): number {
    return this.flash?.intensity ?? 0;
  }

  /** Live particle counts, for the perf overlay. */
  get liveParticles(): number {
    return this.smoke.live + this.fire.live;
  }

  get droppedParticles(): number {
    return this.smoke.dropped + this.fire.dropped;
  }

  /**
   * Scale the effects back on weaker hardware.
   *
   * Emission *rate*, not pool size: shrinking the pools mid-session would strand live particles,
   * and the pools are sized for the worst case anyway. Halving the rate halves the fill cost,
   * which is what actually hurts at Low.
   */
  applyQuality(scale: number): void {
    this.densityScale = clamp01(scale);
  }

  // --- one-shot effects ------------------------------------------------------------------------

  /**
   * Sparks off an impact.
   *
   * Thrown *backwards* along the round's travel, in a cone. Real impact debris comes back at the
   * shooter — it is the round's own material and the target's, and it has nowhere else to go.
   */
  hitSparks(position: Vector3, direction: Vector3, speedMps: number): void {
    const count = Math.round(6 * this.densityScale);
    const back = Math.min(70, speedMps * 0.06);
    for (let i = 0; i < count; i++) {
      scratchVelocity.set(
        -direction.x * back + this.rng.range(-18, 18),
        -direction.y * back + this.rng.range(-18, 18),
        -direction.z * back + this.rng.range(-18, 18),
      );
      this.fire.spawn(position, scratchVelocity, SPARK, this.rng.range(0.6, 1.5));
    }
  }

  /**
   * Something blew up: fireball, smoke column, and a flash of light on everything nearby.
   *
   * @param scale 1 for a drone, ~0.35 for a balloon.
   */
  explosion(position: Vector3, scale = 1): void {
    const fireballs = Math.max(1, Math.round(7 * scale * this.densityScale));
    for (let i = 0; i < fireballs; i++) {
      scratchVelocity.set(this.rng.range(-14, 14), this.rng.range(-6, 16), this.rng.range(-14, 14));
      this.fire.spawn(position, scratchVelocity, FIREBALL, scale * this.rng.range(0.7, 1.3));
    }

    const puffs = Math.max(1, Math.round(10 * scale * this.densityScale));
    for (let i = 0; i < puffs; i++) {
      scratchVelocity.set(this.rng.range(-7, 7), this.rng.range(1, 9), this.rng.range(-7, 7));
      scratchPosition.set(
        position.x + this.rng.range(-3, 3) * scale,
        position.y + this.rng.range(-2, 4) * scale,
        position.z + this.rng.range(-3, 3) * scale,
      );
      this.smoke.spawn(scratchPosition, scratchVelocity, EXPLOSION_SMOKE, scale * this.rng.range(0.7, 1.4));
    }

    this.fireFlash(position, 2_600 * scale);
  }

  /** A dying drone smoking on its way down. Called every frame while the wreck falls. */
  damageSmoke(position: Vector3, velocity: Vector3, rate: number, dt: number): void {
    if (this.rng.nextFloat() > rate * dt * this.densityScale) return;
    scratchVelocity.set(
      velocity.x * 0.2 + this.rng.range(-2, 2),
      velocity.y * 0.2 + this.rng.range(-1, 3),
      velocity.z * 0.2 + this.rng.range(-2, 2),
    );
    this.smoke.spawn(position, scratchVelocity, DAMAGE_SMOKE, this.rng.range(0.7, 1.3));
  }

  /** A missile's trail. `burning` picks the dense motor plume over the thin coasting one. */
  missileTrail(position: Vector3, velocity: Vector3, burning: boolean, dt: number): void {
    const rate = (burning ? 90 : 30) * this.densityScale;
    let budget = rate * dt;
    while (budget > 0) {
      if (budget < 1 && this.rng.nextFloat() > budget) break;
      scratchVelocity.set(this.rng.range(-3, 3), this.rng.range(-3, 3), this.rng.range(-3, 3));
      this.smoke.spawn(position, scratchVelocity, burning ? MOTOR_SMOKE : COAST_SMOKE, this.rng.range(0.7, 1.3));
      budget -= 1;
    }
    if (burning) {
      // The flame sits just behind the missile, moving with it.
      scratchVelocity.copy(velocity).multiplyScalar(0.85);
      this.fire.spawn(position, scratchVelocity, MOTOR_FLAME, this.rng.range(0.8, 1.2));
    }
  }

  // --- continuous effects ----------------------------------------------------------------------

  /**
   * Contrails, if it is cold enough up here.
   *
   * Emitted per *metre flown* rather than per second, so the trail's spacing is a property of the
   * aeroplane's path rather than of the frame rate — the difference between a smooth ribbon and a
   * string of beads that changes density when the frame rate does.
   */
  contrail(
    leftTip: Vector3,
    rightTip: Vector3,
    velocity: Vector3,
    altitudeM: number,
    throttle: number,
    dt: number,
  ): void {
    if (altitudeM < CONTRAIL_ALTITUDE_M || throttle < 0.25) {
      this.contrailAccumulator = 0;
      return;
    }
    /*
     * Fade in over the first few thousand feet above the threshold rather than switching on: the
     * saturation condition is met gradually, and a contrail that appeared instantly at a
     * particular altitude would be the most obvious tell in the simulator.
     */
    const strength = clamp01((altitudeM - CONTRAIL_ALTITUDE_M) / 900) * clamp01(throttle);
    const speed = velocity.length();
    // One puff every 12 m of travel, per wingtip, scaled by how strongly it is forming.
    this.contrailAccumulator += (speed * dt * strength * this.densityScale) / 12;
    while (this.contrailAccumulator >= 1) {
      this.contrailAccumulator -= 1;
      scratchVelocity.set(this.rng.range(-1.5, 1.5), this.rng.range(-1, 1), this.rng.range(-1.5, 1.5));
      this.smoke.spawn(leftTip, scratchVelocity, CONTRAIL, this.rng.range(0.8, 1.2));
      scratchVelocity.set(this.rng.range(-1.5, 1.5), this.rng.range(-1, 1), this.rng.range(-1.5, 1.5));
      this.smoke.spawn(rightTip, scratchVelocity, CONTRAIL, this.rng.range(0.8, 1.2));
    }
  }

  /**
   * Wingtip vortices, if the wing is working hard enough to condense them.
   *
   * The trigger is the *lift coefficient*, approximated by load factor over dynamic pressure and
   * by alpha, because that is what sets the pressure drop in the core. Hence: visible in a hard
   * turn, visible slow and high-alpha on approach, invisible in cruise however fast.
   */
  wingtipVortices(
    leftTip: Vector3,
    rightTip: Vector3,
    velocity: Vector3,
    loadFactor: number,
    alphaRad: number,
    humidity: number,
    dt: number,
  ): void {
    const byG = clamp01((Math.abs(loadFactor) - 3.2) / 4);
    const byAlpha = clamp01((alphaRad - 0.16) / 0.14);
    const strength = Math.max(byG, byAlpha) * clamp01(0.35 + humidity);
    if (strength <= 0.02) {
      this.vortexAccumulator = 0;
      return;
    }

    const speed = velocity.length();
    // One core puff every 6 m: they have to be dense enough to read as a continuous rope.
    this.vortexAccumulator += (speed * dt * strength * this.densityScale) / 6;
    while (this.vortexAccumulator >= 1) {
      this.vortexAccumulator -= 1;
      scratchVelocity.set(this.rng.range(-1, 1), this.rng.range(-1, 1), this.rng.range(-1, 1));
      this.smoke.spawn(leftTip, scratchVelocity, VORTEX, strength * this.rng.range(0.8, 1.2));
      scratchVelocity.set(this.rng.range(-1, 1), this.rng.range(-1, 1), this.rng.range(-1, 1));
      this.smoke.spawn(rightTip, scratchVelocity, VORTEX, strength * this.rng.range(0.8, 1.2));
    }
  }

  // --- frame ------------------------------------------------------------------------------------

  updateFrame(dt: number): void {
    this.smoke.updateFrame(dt);
    this.fire.updateFrame(dt);
    this.updateFlashes(dt);
  }

  /** Carry every particle and every flash through a floating-origin rebase. */
  applyOriginShift(deltaX: number, deltaY: number, deltaZ: number): void {
    this.smoke.applyOriginShift(deltaX, deltaY, deltaZ);
    this.fire.applyOriginShift(deltaX, deltaY, deltaZ);
    if (this.flash !== null && this.flash.intensity > 0) {
      this.flash.position.x += deltaX;
      this.flash.position.y += deltaY;
      this.flash.position.z += deltaZ;
    }
  }

  clear(): void {
    this.smoke.clear();
    this.fire.clear();
    this.tracers.commit(0);
    // Intensity only — never visibility. See the note on `flash`.
    if (this.flash !== null) this.flash.intensity = 0;
    this.flashPeak = 0;
    this.contrailAccumulator = 0;
    this.vortexAccumulator = 0;
  }

  /**
   * Fire the flash, if this one is brighter than whatever is already burning.
   *
   * A dimmer, later explosion does not steal the light from a brighter one still at full
   * intensity — a balloon popping should not dim the drone that just came apart beside it.
   */
  private fireFlash(position: Vector3, peak: number): void {
    const light = this.flash;
    if (light === null || peak <= light.intensity) return;
    light.position.copy(position);
    light.intensity = peak;
    this.flashPeak = peak;
    this.flashAgeS = 0;
  }

  /**
   * Decay the flash.
   *
   * A hard exponential over about a tenth of a second. An explosion's light is a genuine
   * transient — the fireball is bright for a moment and then it is a smoke cloud — and a light
   * that lingered would look like somebody had left a lamp on in the sky.
   *
   * Note what this does *not* do: it never sets `visible`. Below the cutoff the intensity is
   * snapped to exactly zero, which costs the shader nothing it was not already paying and costs
   * the pipeline cache nothing at all.
   */
  private updateFlashes(dt: number): void {
    const light = this.flash;
    if (light === null || light.intensity === 0) return;
    this.flashAgeS += dt;
    const fade = Math.exp(-this.flashAgeS / FLASH_DECAY_S);
    const intensity = this.flashPeak * fade;
    light.intensity = intensity < FLASH_CUTOFF ? 0 : intensity;
  }

  dispose(): void {
    this.smoke.dispose();
    this.fire.dispose();
    this.tracers.dispose();
  }
}
